import { amcPalette, amcSemanticColors, useTheme } from "@amx/ux-twoui-theming";
import { ProgressIndicator as FluentProgressIndicator } from "@fluentui/react";
import React from "react";
import { useComponentLogger } from "../../shared/logging";
import { TextTypes } from "../../styles";
import { Icon, IconProps } from "../Icon/Icon";
import { RichText, RichTextProps } from "../RichText/RichText";
import { Stack } from "../Stacks/Stack/Stack";
import { TruncatedText } from "../TruncatedText/TruncatedText";
import { getProgressIndicatorStyles } from "./ProgressIndicator.styles";

export type BarHeightType = "thick" | "thin";
export type UsageIndicatorSize = "wide" | "narrow";

const progressIndicatorThickBarHeight: number = 8;
const progressIndicatorThinBarHeight: number = 4;
const progressIndicatorDeterminateBarHeight: number = 4;
const progressIndicatorIndeterminateBarHeight: number = 2;

type TrackColor = keyof typeof amcPalette | keyof typeof amcSemanticColors;
type BarColor = keyof typeof amcPalette | keyof typeof amcSemanticColors;

export type LabelTextVariant = Extract<TextTypes, "headerText" | "bodyPrimaryText">;

export interface ProgressIndicatorProps {
    /**
     * Label to display above the control.
     */
    readonly label?: string | RichTextProps;
    /**
     * Text variant of the label.
     */
    readonly labelTextVariant?: LabelTextVariant;
    /**
     * Text describing or supplementing the operation.
     */
    readonly description?: string;
    /**
     * Percentage of the operation's completeness, numerically between 0 and 1. If this is not set, the indeterminate progress animation will be shown instead.
     */
    readonly percentComplete?: number;
    /**
     * Text alternative of the progress status, used by screen readers for reading the value of the progress.
     */
    readonly ariaValueText: string;
    /**
     * Optional screen-reader-only label text to the progressbar.
     * Prefer `label`, and use this only when other text or visual context provides a visible label or there is no label provided.
     */
    readonly ariaLabel?: string;
    /**
     * Optional icon to show
     */
    readonly icon?: IconProps;
    /**
     * Optional richText for usage information
     */
    readonly usage?: RichTextProps;
    /**
     * Optional progress bar fill color
     */
    readonly barColor?: BarColor;
    /**
     * Optional progress bar track color
     */
    readonly trackColor?: TrackColor;
    /**
     * Optional thickness for progress bar
     */
    readonly barType?: BarHeightType;
    /**
     * Whether to stack label text and usage text
     */
    readonly isStackStyle?: boolean;
    /**
     * Optional widthness for progress bar
     */
    readonly size?: UsageIndicatorSize;
}

const getRichText = (labelProps: RichTextProps) => {
    return (
        <Stack>
            <RichText
                textWithTokens={labelProps.textWithTokens}
                tokensAndValues={labelProps.tokensAndValues}
                truncatedTextProps={labelProps.truncatedTextProps}
            />
        </Stack>
    );
};

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
    label,
    labelTextVariant = "headerText",
    description,
    percentComplete,
    ariaValueText,
    ariaLabel,
    icon,
    usage,
    barColor = "themePrimary",
    trackColor = "neutralLight",
    barType,
    isStackStyle,
    size
}) => {
    useComponentLogger("ProgressIndicator");

    const theme = useTheme();

    let progressIndicatorBarHeight;
    if (barType) {
        progressIndicatorBarHeight = barType === "thick" ? progressIndicatorThickBarHeight : progressIndicatorThinBarHeight;
    } else {
        if (percentComplete || percentComplete === 0) {
            progressIndicatorBarHeight = progressIndicatorDeterminateBarHeight;
        } else {
            progressIndicatorBarHeight = progressIndicatorIndeterminateBarHeight;
        }
    }

    const progressBarColor = theme.palette[barColor] ?? theme.semanticColors[barColor];
    const progressTrackColor = theme.palette[trackColor] ?? theme.semanticColors[trackColor];

    const getLabelContent = () => {
        if (label) {
            if (typeof label === "string") {
                return (
                    <Stack>
                        <TruncatedText lines={2} variant={labelTextVariant}>{label}</TruncatedText>
                    </Stack>
                );
            } else {
                return getRichText(label);
            }
        } else {
            return null;
        }
    };

    const descriptionText = <TruncatedText lines={3} variant="metadataText">{description}</TruncatedText>;

    const progressBarContent = () => {
        return (
            <Stack
                horizontal={!isStackStyle}
                horizontalAlign="space-between"
                wrap={true}
            >
                <Stack horizontal={true} childrenGap={5}>
                    {
                        icon && !isStackStyle &&
                        <Icon iconName={icon.iconName} size={icon.size} color={icon.color} disabled={icon.disabled} />
                    }
                    {getLabelContent()}
                </Stack>
                {usage && getRichText(usage)}
            </Stack>
        );
    };

    return (
        <FluentProgressIndicator
            styles={getProgressIndicatorStyles(theme, size, progressBarColor, progressTrackColor, barType)}
            label={progressBarContent()}
            description={descriptionText}
            percentComplete={percentComplete}
            ariaValueText={ariaValueText}
            barHeight={progressIndicatorBarHeight}
            ariaLabel={ariaLabel}
        />
    );
};
